import React, { useState, useEffect } from 'react';
import { Download, FileSpreadsheet, Printer, Search, RefreshCw, Filter } from 'lucide-react';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getManufacturingCompanyStatement } from '../../api/wholesale/reports';
import { useBranding } from '../../contexts/BrandingContext';
import { useFiscalPeriod } from '../../contexts/FiscalPeriodContext';
import { toast } from 'react-toastify';
import PeriodFilter from '../common/PeriodFilter';
import Pagination from '../common/Pagination';

const ManufacturingCompanyStatement = ({
  companyId,
  companyName,
  includeStandaloneExpenses = true,
  includeSalaries = true,
  includeOrderExpenses = true
}) => {
  const { config: branding } = useBranding();
  const { periodRange, getDateRangeParams } = useFiscalPeriod();
  const [localPeriodRange, setLocalPeriodRange] = useState(null);

  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [highlightedOrderId, setHighlightedOrderId] = useState(null);
  const [highlightedPayrollRef, setHighlightedPayrollRef] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const itemsPerPage = 50;

  const fetchStatement = async () => {
    setLoading(true);
    setError(null);
    try {
      // Parse companyId and reportType from prop
      let apiCompanyId = companyId;
      let reportType = 'specific';
      let companyIdsParam = undefined;

      if (Array.isArray(companyId)) {
        apiCompanyId = 'all';
        reportType = 'specific';
        companyIdsParam = companyId.join(',');
      } else if (companyId === 'all_general' || companyId === 'all') {
        apiCompanyId = 'all';
        reportType = 'general';
      } else if (companyId === 'all_manufacturing') {
        apiCompanyId = 'all';
        reportType = 'combined';
      }

      let params = {
        ...getDateRangeParams(),
        page: currentPage,
        limit: itemsPerPage,
        report_type: reportType,
        include_standalone_expenses: includeStandaloneExpenses,
        include_salaries: includeSalaries,
        include_order_expenses: includeOrderExpenses,
        sort_order: sortOrder
      };
      if (companyIdsParam !== undefined) {
        params.company_ids = companyIdsParam;
      }
      if (localPeriodRange && !localPeriodRange.isGlobal) {
        params = {
          ...params,
          start_date: localPeriodRange.start_date,
          end_date: localPeriodRange.end_date,
          date_from: localPeriodRange.start_date,
          date_to: localPeriodRange.end_date
        };
      }
      const data = await getManufacturingCompanyStatement(apiCompanyId, params);
      setStatement(data.data || data);
    } catch (err) {
      console.error('Error fetching statement:', err);
      setError('Failed to load statement data.');
      toast.error('Failed to load statement.');
    } finally {
      setLoading(false);
    }
  };

  const companyIdWatch = Array.isArray(companyId) ? companyId.join(',') : companyId;

  useEffect(() => {
    if (companyId) {
      if (
        typeof companyId === 'number' ||
        (typeof companyId === 'string' &&
          companyId !== 'all' &&
          companyId !== 'all_general' &&
          companyId !== 'all_manufacturing')
      ) {
        const id = parseInt(companyId);
        if (isNaN(id)) return;
      }
      fetchStatement();
    }
  }, [
    companyIdWatch,
    periodRange,
    localPeriodRange,
    currentPage,
    includeStandaloneExpenses,
    includeSalaries,
    includeOrderExpenses,
    sortOrder
  ]);

  // Reset page when period or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    periodRange,
    localPeriodRange,
    includeStandaloneExpenses,
    includeSalaries,
    includeOrderExpenses
  ]);

  const generatePDF = async () => {
    if (!statement) return;

    try {
      toast.info('Preparing full report for download...');

      // Parse companyId and reportType from prop
      let apiCompanyId = companyId;
      let reportType = 'specific';
      let companyIdsParam = undefined;

      if (Array.isArray(companyId)) {
        apiCompanyId = 'all';
        reportType = 'specific';
        companyIdsParam = companyId.join(',');
      } else if (companyId === 'all_general' || companyId === 'all') {
        apiCompanyId = 'all';
        reportType = 'general';
      } else if (companyId === 'all_manufacturing') {
        apiCompanyId = 'all';
        reportType = 'combined';
      }

      // Fetch full set for export (no pagination)
      let params = {
        ...getDateRangeParams(),
        limit: 10000,
        report_type: reportType,
        include_standalone_expenses: includeStandaloneExpenses,
        include_salaries: includeSalaries,
        include_order_expenses: includeOrderExpenses,
        sort_order: sortOrder
      };
      if (companyIdsParam !== undefined) {
        params.company_ids = companyIdsParam;
      }
      if (localPeriodRange && !localPeriodRange.isGlobal) {
        params = {
          ...params,
          date_from: localPeriodRange.start_date,
          date_to: localPeriodRange.end_date
        };
      }

      const fullDataResponse = await getManufacturingCompanyStatement(apiCompanyId, params);
      const fullStatement = fullDataResponse.data || fullDataResponse;

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const accentColor = branding.accent_color || '#D4AF37';
      const [r, g, b] = [
        parseInt(accentColor.slice(1, 3), 16),
        parseInt(accentColor.slice(3, 5), 16),
        parseInt(accentColor.slice(5, 7), 16)
      ];

      // Header
      doc.setFontSize(22);
      doc.setTextColor(r, g, b);
      doc.text(branding.company_name || 'Eldaved Global', 14, 20);

      doc.setFontSize(16);
      doc.setTextColor(40, 40, 40);
      doc.text(
        reportType === 'general' ? 'GENERAL STATEMENT OF ACCOUNT' : 'STATEMENT OF ACCOUNT',
        14,
        30
      );

      doc.setFontSize(11);
      doc.setTextColor(80, 80, 80);
      if (companyId !== 'all' && companyId !== 'all_general' && companyId !== 'all_manufacturing') {
        doc.text(`Manufacturing Company: ${companyName}`, 14, 40);
      } else {
        doc.text(`Companies: ${companyName || 'All Factories Combined'}`, 14, 40);
      }

      let dateParams = getDateRangeParams();
      if (localPeriodRange && !localPeriodRange.isGlobal) {
        dateParams = {
          date_from: localPeriodRange.start_date,
          date_to: localPeriodRange.end_date
        };
      }
      let periodText = 'All Time';
      if (dateParams.date_from && dateParams.date_to) {
        periodText = `${format(new Date(dateParams.date_from), 'MMM dd, yyyy')} to ${format(new Date(dateParams.date_to), 'MMM dd, yyyy')}`;
      } else if (dateParams.date_from) {
        periodText = `From ${format(new Date(dateParams.date_from), 'MMM dd, yyyy')}`;
      }
      doc.text(`Period: ${periodText}`, 14, 46);
      doc.text(`Generated: ${format(new Date(), 'MMM dd, yyyy HH:mm')}`, 14, 52);

      // Summary Table
      autoTable(doc, {
        startY: 60,
        head: [
          [
            'Opening Balance',
            'Total Loadings',
            'Total Payments',
            'Total Expenses',
            'Total Payroll',
            'Total Rebates',
            'Closing Balance'
          ]
        ],
        body: [
          [
            `N ${(fullStatement.opening_balance || 0).toLocaleString()}`,
            `N ${(fullStatement.summary?.total_loading || 0).toLocaleString()}`,
            `N ${(fullStatement.summary?.total_payment || 0).toLocaleString()}`,
            `N ${(fullStatement.summary?.total_expenses || 0).toLocaleString()}`,
            `N ${(fullStatement.summary?.total_payroll || 0).toLocaleString()}`,
            `N ${(fullStatement.summary?.total_rebates || 0).toLocaleString()}`,
            `N ${(fullStatement.closing_balance || 0).toLocaleString()}`
          ]
        ],
        theme: 'grid',
        headStyles: { fillColor: [240, 240, 240], textColor: [40, 40, 40], fontStyle: 'bold' },
        styles: { halign: 'right' }
      });

      // Detail Table
      const tableColumn = [
        'Date',
        'Type',
        'Ref / Waybill',
        'Description / Customer',
        'Qty (T)',
        'Loading (N)',
        'Payment (N)',
        'Expense/Rebate',
        'Balance (N)'
      ];

      const tableRows = fullStatement.timeline.map((item) => {
        let typeLabel = '';
        let desc = item.remark || '';
        let amountLoading = '';
        let amountPayment = '';
        let amountExpReb = '';
        let qty = '-';

        if (item.type === 'loading') {
          typeLabel = 'Loading';
          desc = item.distributor_name || '';
          if (item.vehicle_number) {
            desc += `\n🚗 ${item.vehicle_number}`;
          }
          amountLoading =
            parseFloat(item.loading_amount) > 0
              ? `${parseFloat(item.loading_amount).toLocaleString()}`
              : '-';
          qty = item.qty ? parseFloat(item.qty).toFixed(2) : '-';
        } else if (
          item.type === 'payment' ||
          item.type === 'wallet_payment' ||
          item.type === 'account_deposit'
        ) {
          if (item.type === 'wallet_payment') typeLabel = 'Wallet Payment';
          else if (item.type === 'account_deposit') typeLabel = 'Account Deposit';
          else typeLabel = 'Payment';

          amountPayment =
            parseFloat(item.payment_amount) > 0
              ? `${parseFloat(item.payment_amount).toLocaleString()}`
              : '-';
          desc =
            item.remark ||
            (item.type === 'wallet_payment' ? 'Payment from Account' : 'Standalone Payment');
        } else if (item.type === 'expense') {
          typeLabel = 'Expense';
          amountExpReb = `-${(parseFloat(item.expense_amount) || 0).toLocaleString()}`;
        } else if (item.type === 'payroll') {
          typeLabel = 'Payroll';
          amountExpReb = `-${(parseFloat(item.payroll_amount) || 0).toLocaleString()}`;
        } else if (item.type === 'rebate') {
          typeLabel = 'Rebate';
          amountExpReb = `+${(parseFloat(item.rebate_amount) || 0).toLocaleString()}`;
        }

        const qtyCell =
          apiCompanyId === 'all' && item.company_name
            ? `${item.qty ? parseFloat(item.qty).toFixed(2) : '-'}\n(${item.company_name})`
            : item.qty
              ? parseFloat(item.qty).toFixed(2)
              : '-';

        return [
          format(new Date(item.date), 'MMM dd, yyyy'),
          typeLabel,
          item.type === 'loading'
            ? `${item.reference || ''}\n${item.waybill_number || ''}`
            : item.reference || '',
          desc,
          qtyCell,
          amountLoading,
          amountPayment,
          amountExpReb,
          (item.running_balance || 0).toLocaleString()
        ];
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 10,
        head: [tableColumn],
        body: tableRows,
        theme: 'striped',
        headStyles: { fillColor: [r, g, b], textColor: 255 },
        styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak' },
        columnStyles: {
          4: { halign: 'left' },
          5: { halign: 'left' },
          6: { halign: 'left' },
          7: { halign: 'left' },
          8: { halign: 'left', fontStyle: 'bold' }
        },
        margin: { top: 15, right: 10, bottom: 15, left: 10 },
        tableWidth: 'auto',
        horizontalPageBreak: false
      });

      const safeCompanyName = companyName ? companyName.replace(/\s+/g, '_') : 'General';
      doc.save(`${safeCompanyName}_Statement_${format(new Date(), 'yyyyMMdd')}.pdf`);
      toast.success('PDF downloaded successfully');
    } catch (err) {
      console.error('Error generating PDF:', err);
      toast.error('Failed to generate PDF');
    }
  };

  const generateCSV = async () => {
    if (!statement) return;

    try {
      toast.info('Preparing full report for download...');

      // Parse companyId and reportType from prop
      let apiCompanyId = companyId;
      let reportType = 'specific';
      let companyIdsParam = undefined;

      if (Array.isArray(companyId)) {
        apiCompanyId = 'all';
        reportType = 'specific';
        companyIdsParam = companyId.join(',');
      } else if (companyId === 'all_general' || companyId === 'all') {
        apiCompanyId = 'all';
        reportType = 'general';
      } else if (companyId === 'all_manufacturing') {
        apiCompanyId = 'all';
        reportType = 'combined';
      }

      // Fetch full set for export (no pagination)
      let params = {
        ...getDateRangeParams(),
        limit: 10000,
        report_type: reportType,
        include_standalone_expenses: includeStandaloneExpenses,
        include_salaries: includeSalaries,
        include_order_expenses: includeOrderExpenses,
        sort_order: sortOrder
      };
      if (companyIdsParam !== undefined) {
        params.company_ids = companyIdsParam;
      }
      if (localPeriodRange && !localPeriodRange.isGlobal) {
        params = {
          ...params,
          date_from: localPeriodRange.start_date,
          date_to: localPeriodRange.end_date
        };
      }

      const fullDataResponse = await getManufacturingCompanyStatement(apiCompanyId, params);
      const fullStatement = fullDataResponse.data || fullDataResponse;

      const headers = [
        'Date',
        'Type',
        'Ref',
        'Waybill',
        'Customer/Desc',
        'Qty(T)',
        'Loading',
        'Payment',
        'Expense',
        'Payroll',
        'Rebate',
        'Balance'
      ];
      const rows = fullStatement.timeline.map((item) => [
        format(new Date(item.date), 'yyyy-MM-dd'),
        item.type,
        item.reference || '',
        item.waybill_number || '',
        `"${(item.type === 'loading' ? item.distributor_name : item.remark) || ''}"`,
        item.qty || '',
        item.type === 'loading' ? item.loading_amount : '',
        item.type === 'payment' || item.type === 'wallet_payment' || item.type === 'account_deposit'
          ? item.payment_amount
          : '',
        item.type === 'expense'
          ? item.expense_amount
          : item.type === 'payroll'
            ? item.payroll_amount
            : item.type === 'rebate'
              ? item.rebate_amount
              : '',
        item.running_balance
      ]);

      const csvContent = [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      const safeCompanyName = companyName ? companyName.replace(/\s+/g, '_') : 'General';
      link.setAttribute(
        'download',
        `${safeCompanyName}_Statement_${format(new Date(), 'yyyyMMdd')}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error generating CSV:', err);
      toast.error('Failed to generate CSV');
    }
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm text-center">
        {error}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden min-h-[400px] relative">
      {/* Loading Overlay */}
      {loading && !statement && (
        <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] z-10 flex justify-center items-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-emerald-500 border-t-transparent"></div>
        </div>
      )}
      {/* Header Actions */}
      <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <button
            onClick={fetchStatement}
            className="p-2 text-gray-500 hover:text-accent-gold hover:bg-accent-gold/10 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors flex items-center gap-1.5 select-none font-medium"
            title={`Sorting: ${sortOrder === 'asc' ? 'Oldest First' : 'Newest First'}`}
          >
            Sort: {sortOrder === 'asc' ? 'Oldest ➔ Newest' : 'Newest ➔ Oldest'}
          </button>
          <span className="text-sm text-gray-600 border-l border-gray-300 px-2">
            Statement of Account
          </span>
          <PeriodFilter
            onChange={setLocalPeriodRange}
            storageKey="mfg_statement_period"
            compact={true}
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            onClick={generateCSV}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
          >
            <FileSpreadsheet className="w-4 h-4 text-green-600" />
            CSV Export
          </button>
          <button
            onClick={generatePDF}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-3 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors text-sm font-medium shadow-sm"
          >
            <Download className="w-4 h-4" />
            Download PDF
          </button>
        </div>
      </div>

      {/* Summary Grid */}
      {!statement && !loading ? (
        <div className="p-8 text-center text-gray-500">No data available</div>
      ) : (
        <>
          <div
            className={`flex flex-col md:flex-row border-b border-gray-200 bg-white transition-opacity ${loading ? 'opacity-50' : 'opacity-100'}`}
          >
            <div className="p-4 flex-1 border-r border-b md:border-b-0 border-gray-200">
              <p className="text-xs text-gray-500 mb-1">
                {currentPage === 1 ? 'Opening Balance' : 'Page Op. Balance'}
              </p>
              <p
                className={`text-lg font-bold ${
                  (statement?.page_opening_balance || statement?.opening_balance || 0) > 0
                    ? 'text-green-600'
                    : (statement?.page_opening_balance || statement?.opening_balance || 0) < 0
                      ? 'text-red-600'
                      : 'text-gray-900'
                }`}
              >
                ₦
                {Math.round(
                  statement?.page_opening_balance || statement?.opening_balance || 0
                ).toLocaleString()}
              </p>
            </div>
            <div className="p-4 flex-1 border-r border-b md:border-b-0 border-gray-200">
              <p className="text-xs text-blue-600 mb-1">Total Loadings</p>
              <p className="text-lg font-bold text-gray-900">
                ₦{(statement?.summary?.total_loading || 0).toLocaleString()}
              </p>
            </div>
            <div className="p-4 flex-1 border-r border-b md:border-b-0 border-gray-200">
              <p className="text-xs text-emerald-600 mb-1">Total Paid</p>
              <p className="text-lg font-bold text-emerald-600">
                ₦{(statement?.summary?.total_payment || 0).toLocaleString()}
              </p>
            </div>
            <div className="p-4 flex-1 border-r border-b md:border-b-0 border-gray-200">
              <p className="text-xs text-orange-600 mb-1">Expenses</p>
              <p className="text-lg font-bold text-gray-900">
                ₦{(statement?.summary?.total_expenses || 0).toLocaleString()}
              </p>
            </div>
            {statement?.summary?.total_payroll !== undefined && (
              <div className="p-4 flex-1 border-r border-b md:border-b-0 border-gray-200">
                <p className="text-xs text-red-600 mb-1">Payroll</p>
                <p className="text-lg font-bold text-gray-900">
                  ₦{(statement?.summary?.total_payroll || 0).toLocaleString()}
                </p>
              </div>
            )}
            <div className="p-4 flex-1 border-r border-b md:border-b-0 border-gray-200">
              <p className="text-xs text-purple-600 mb-1">Rebates</p>
              <p className="text-lg font-bold text-gray-900">
                ₦{(statement?.summary?.total_rebates || 0).toLocaleString()}
              </p>
            </div>
            <div className="p-4 flex-1 bg-gray-50">
              <p className="text-xs text-gray-700 font-bold mb-1">Closing Balance</p>
              <p
                className={`text-xl font-black ${(statement?.closing_balance || 0) > 0 ? 'text-green-600' : (statement?.closing_balance || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}
              >
                ₦{Math.round(statement?.closing_balance || 0).toLocaleString()}
              </p>
            </div>
          </div>

          {/* Timeline Table */}
          <div
            className={`overflow-x-auto transition-opacity ${loading ? 'opacity-50' : 'opacity-100'}`}
          >
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Type / Ref
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Details
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Qty (T)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Loading (₦)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Payment (₦)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Other (₦)
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase font-bold">
                    Balance (₦)
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {!statement?.timeline || statement.timeline.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-gray-500 text-sm">
                      {loading
                        ? 'Refreshing data...'
                        : 'No transactions found for the selected period.'}
                    </td>
                  </tr>
                ) : (
                  statement.timeline.map((item, idx) => {
                    const cleanRef = (ref) => (ref ? ref.replace(' (Reversal)', '').trim() : '');
                    const isPayrollHilited =
                      highlightedPayrollRef &&
                      item.reference &&
                      cleanRef(item.reference) === cleanRef(highlightedPayrollRef);

                    const isHighlighted =
                      (highlightedOrderId &&
                        ((item.type === 'loading' && item.id === highlightedOrderId) ||
                          (item.type === 'payment' &&
                            item.wholesale_order_id === highlightedOrderId) ||
                          (item.type === 'expense' &&
                            item.wholesale_order_id === highlightedOrderId))) ||
                      isPayrollHilited;
                    const isUnpaidLoading =
                      item.type === 'loading' &&
                      parseFloat(item.amount_paid || 0) < parseFloat(item.loading_amount || 0);

                    const hiliteStyle = isHighlighted
                      ? {
                          backgroundColor: branding.accent_color
                            ? `${branding.accent_color}20`
                            : '#D4AF3720',
                          transition: 'background-color 0.3s ease'
                        }
                      : { transition: 'background-color 0.3s ease' };

                    const cellBgClass = isHighlighted
                      ? ''
                      : isUnpaidLoading
                        ? 'bg-red-50 dark:bg-red-50'
                        : 'bg-white dark:bg-slate-900';

                    return (
                      <tr
                        key={`${item.type}-${item.id}-${idx}`}
                        className={`border-b border-gray-100 dark:border-slate-800 transition-colors duration-200 ${
                          isHighlighted
                            ? ''
                            : isUnpaidLoading
                              ? 'border-red-200 dark:border-red-200'
                              : 'hover:bg-gray-50 dark:hover:bg-slate-800/80'
                        }`}
                        style={hiliteStyle}
                      >
                        <td
                          className={`px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400 ${cellBgClass}`}
                        >
                          {format(new Date(item.date), 'MMM dd, yyyy')}
                        </td>
                        <td className={`px-4 py-3 text-sm ${cellBgClass}`}>
                          {item.type === 'loading' && (
                            <div>
                              <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 mb-1">
                                Loading
                              </span>
                              <div className="text-gray-900 dark:text-gray-100 font-medium">
                                {item.reference}
                              </div>
                            </div>
                          )}
                          {item.type === 'expense' && (
                            <div>
                              <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-700 mb-1">
                                Expense
                              </span>
                              <div className="text-gray-500 text-xs">{item.reference}</div>
                            </div>
                          )}
                          {(item.type === 'payroll' || item.type === 'payroll_reversal') && (
                            <div>
                              <span
                                className={`inline-flex px-2 py-0.5 rounded text-[10px] font-medium mb-1 ${
                                  item.type === 'payroll_reversal'
                                    ? 'bg-emerald-100 text-emerald-800 font-bold'
                                    : 'bg-red-100 text-red-700'
                                }`}
                              >
                                {item.type === 'payroll_reversal' ? 'Payroll Reversal' : 'Payroll'}
                              </span>
                              <div className="text-gray-500 text-xs font-mono font-medium">
                                {item.reference}
                              </div>
                            </div>
                          )}
                          {(item.type === 'payment' ||
                            item.type === 'wallet_payment' ||
                            item.type === 'account_deposit') && (
                            <div>
                              <span
                                className={`inline-flex px-2 py-0.5 rounded text-[10px] font-medium mb-1 ${
                                  item.type === 'wallet_payment'
                                    ? 'bg-purple-100 text-purple-700'
                                    : item.type === 'account_deposit'
                                      ? 'bg-blue-100 text-blue-700'
                                      : 'bg-emerald-100 text-emerald-700'
                                }`}
                              >
                                {item.type === 'wallet_payment'
                                  ? 'Wallet Payment'
                                  : item.type === 'account_deposit'
                                    ? 'Account Deposit'
                                    : 'Payment'}
                              </span>
                              <div className="text-gray-500 text-xs">{item.reference}</div>
                            </div>
                          )}
                          {item.type === 'rebate' && (
                            <div>
                              <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700">
                                Rebate
                              </span>
                            </div>
                          )}
                        </td>
                        <td
                          className={`px-4 py-3 text-sm text-gray-700 dark:text-gray-300 max-w-xs ${cellBgClass}`}
                        >
                          {item.type === 'loading' ? (
                            <div>
                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                {item.distributor_name}
                              </span>
                              {item.vehicle_number && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1">
                                  🚗 {item.vehicle_number}
                                </div>
                              )}
                            </div>
                          ) : item.type === 'payment' ||
                            item.type === 'wallet_payment' ||
                            item.type === 'account_deposit' ? (
                            <div>
                              <div className="text-gray-900">
                                {item.remark ||
                                  (item.type === 'wallet_payment'
                                    ? 'Payment from Account'
                                    : 'Standalone Payment')}
                              </div>
                              {item.order_reference && (
                                <button
                                  onClick={() =>
                                    setHighlightedOrderId(
                                      item.wholesale_order_id === highlightedOrderId
                                        ? null
                                        : item.wholesale_order_id
                                    )
                                  }
                                  className="mt-1 flex items-center gap-1 text-[10px] text-emerald-600 font-medium bg-emerald-50 px-1.5 py-0.5 rounded w-fit border border-emerald-100 hover:bg-emerald-100 transition-colors cursor-pointer"
                                >
                                  <Search className="w-2.5 h-2.5" />
                                  Linked to order: {item.order_reference}
                                </button>
                              )}
                            </div>
                          ) : item.type === 'payroll' || item.type === 'payroll_reversal' ? (
                            <div>
                              <div className="text-gray-900 font-medium">{item.remark}</div>
                              {item.reference && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const cleanRef = (r) =>
                                      r ? r.replace(' (Reversal)', '').trim() : '';
                                    setHighlightedPayrollRef(
                                      cleanRef(highlightedPayrollRef) === cleanRef(item.reference)
                                        ? null
                                        : item.reference
                                    );
                                  }}
                                  className={`mt-1 flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded w-fit border transition-colors cursor-pointer ${
                                    highlightedPayrollRef &&
                                    item.reference.replace(' (Reversal)', '').trim() ===
                                      highlightedPayrollRef.replace(' (Reversal)', '').trim()
                                      ? 'bg-amber-100 text-amber-800 border-amber-300 font-bold'
                                      : item.type === 'payroll_reversal'
                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                        : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                                  }`}
                                >
                                  <Search className="w-2.5 h-2.5" />
                                  {item.type === 'payroll_reversal'
                                    ? `Linked to initial entry (${item.reference.replace(' (Reversal)', '')})`
                                    : `Linked to payroll run (${item.reference})`}
                                </button>
                              )}
                            </div>
                          ) : (
                            <div>
                              <div className="text-gray-900">{item.remark}</div>
                              {item.wholesale_order_id && (
                                <button
                                  onClick={() =>
                                    setHighlightedOrderId(
                                      item.wholesale_order_id === highlightedOrderId
                                        ? null
                                        : item.wholesale_order_id
                                    )
                                  }
                                  className="mt-1 flex items-center gap-1 text-[10px] text-emerald-600 font-medium bg-emerald-50 px-1.5 py-0.5 rounded w-fit border border-emerald-100 hover:bg-emerald-100 transition-colors cursor-pointer"
                                >
                                  <Search className="w-2.5 h-2.5" />
                                  Linked to loading (ID: {item.wholesale_order_id})
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        <td
                          className={`px-4 py-3 text-left text-sm text-gray-600 dark:text-gray-400 ${cellBgClass}`}
                        >
                          <div className="font-medium text-gray-900 dark:text-gray-100">
                            {item.qty ? parseFloat(item.qty).toFixed(2) : '-'}
                          </div>
                          {(companyId === 'all' ||
                            companyId === 'all_general' ||
                            companyId === 'all_manufacturing') &&
                            item.company_name && (
                              <div className="text-[10px] text-blue-600 dark:text-blue-400 font-medium uppercase mt-0.5">
                                {item.company_name}
                              </div>
                            )}
                        </td>
                        <td
                          className={`px-4 py-3 text-left text-sm font-medium text-gray-900 dark:text-gray-100 ${cellBgClass}`}
                        >
                          {item.type === 'loading' && parseFloat(item.loading_amount) > 0
                            ? `${parseFloat(item.loading_amount).toLocaleString()}`
                            : '-'}
                        </td>
                        <td className={`px-4 py-3 text-left text-sm font-medium ${cellBgClass}`}>
                          {(item.type === 'payment' ||
                            item.type === 'wallet_payment' ||
                            item.type === 'account_deposit' ||
                            item.type === 'payroll_reversal') &&
                          parseFloat(item.payment_amount || item.payroll_amount || 0) > 0 ? (
                            <span
                              className={
                                item.type === 'wallet_payment'
                                  ? 'text-purple-600 font-semibold'
                                  : item.type === 'payroll_reversal'
                                    ? 'text-emerald-700 font-bold'
                                    : 'text-emerald-600 font-semibold'
                              }
                            >
                              +
                              {parseFloat(
                                item.payment_amount || item.payroll_amount || 0
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })}
                            </span>
                          ) : item.type === 'loading' &&
                            parseFloat(item.amount_paid || 0) <
                              parseFloat(item.loading_amount || 0) ? (
                            <span
                              className="text-amber-500 font-bold bg-amber-500/10 px-1.5 py-0.5 rounded text-xs select-none"
                              title="Pending Balance"
                            >
                              Bal: ₦
                              {Math.round(
                                parseFloat(item.loading_amount) - parseFloat(item.amount_paid)
                              ).toLocaleString()}
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className={`px-4 py-3 text-left text-sm ${cellBgClass}`}>
                          {item.type === 'expense' && (
                            <span className="text-orange-600 font-medium">
                              -{parseFloat(item.expense_amount).toLocaleString()}
                            </span>
                          )}
                          {item.type === 'payroll' && (
                            <span className="text-red-600 font-medium">
                              -{parseFloat(item.payroll_amount).toLocaleString()}
                            </span>
                          )}
                          {item.type === 'rebate' && (
                            <span className="text-purple-600 font-medium">
                              +{parseFloat(item.rebate_amount).toLocaleString()}
                            </span>
                          )}
                          {(item.type === 'loading' || item.type === 'payroll_reversal') && '-'}
                        </td>
                        <td
                          className={`px-4 py-3 text-left text-sm font-bold border-l border-gray-100 dark:border-slate-800 ${
                            isUnpaidLoading
                              ? 'bg-red-50 dark:bg-red-50'
                              : 'bg-gray-50/50 dark:bg-slate-800/50'
                          } ${
                            (item.running_balance || 0) > 0
                              ? 'text-green-600 dark:text-green-400'
                              : (item.running_balance || 0) < 0
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-gray-900 dark:text-gray-100'
                          }`}
                        >
                          {(item.running_balance || 0).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          {statement?.total_pages > 1 && (
            <div className="border-t border-gray-200">
              <Pagination
                currentPage={currentPage}
                totalPages={statement.total_pages}
                totalItems={statement.total_items}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                itemLabel="transactions"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ManufacturingCompanyStatement;
